import React, { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router";

import EditButton from "../../../components/buttons/edit-button/EditButton";
import DisplayDateCreated from "../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../components/data-components/DisplayCreatedBy";
import {
  DisplaySewerObjectId,
  DisplayDataOwner,
  DisplayInProject,
  DisplayOpenJobs,
} from "../components/DataComponents";

import {
  ActionsSection,
  ButtonsSection,
  SubSection,
  DataSection,
} from "../../../components/data-components/sections/DataSections";

import { DisplayDynamicProperties } from "../../../components/data-components/DisplayDynamicProperties";
import DeleteButton from "../../../components/buttons/delete-button/DeleteButton";
import { CurrentStructureStorageModel } from "../../../services/sewer-object/CurrentStructureStorage";
import PageLayout from "../../_layout/PageLayout";
import { StructureMenuItemsModel } from "./StructureMenuItems";
import {
  EditSewerObject,
  DeleteSewerObject,
  ViewSewerObject,
  ViewSewerObjectFromAssignedProject,
  ManageDeletedData,
  EditSewerObjectFromAssignedProject,
  DeleteSewerObjectsFromAssignedProject,
} from "../../../services/authorization/Permissions";
import { api } from "../../../services/api/ApiService";
import useUrlParams from "../../../services/url/UrlParamsService";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { snackbarNotificationsService } from "../../../services/notifications/SnackbarNotificationsService";
import useErrorHandler, {
  ApplicationError,
} from "../../../services/error-handler/ErrorHandlerService";
import GenericObjectIssues from "../../../components/generic-object-issues/GenericObjectIssues";
import { appSessionStorage } from "../../../services/storage/SessionStorageService";
import { CollapsibleServiceModel } from "../../../services/collapsible/CollapsibleService";
import { navigatePath } from "../../../helpers/StringHelpers";
import { breadcrumbsStorage } from "../../../services/breadcrumbs/BreadcrumbsStorage";
import { EntityTypeFromPath } from "../../../services/breadcrumbs/EntityTypeFromPathModel";
import useRouteService from "../../../services/route-service/RouteService";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const [currentStructureStorage] = useState(() =>
    CurrentStructureStorageModel.create({
      structureId: urlParamsService.getStructureId()!,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const collapsibleService = CollapsibleServiceModel.create(
    { collapsedContentKey: "genericIssuesStructureIsCollapsed" },
    { appSessionStorage }
  );

  const menuItems = useMemo(() => {
    const structureMenuItems = StructureMenuItemsModel.create(
      {
        structureId: currentStructureStorage.structureId,
        parentIdFromPath: currentStructureStorage.parentEntityFromPath.id,
        shouldDisableMenuItems: currentStructureStorage.isDeleteInProgress,
      },
      { api }
    );

    return {
      staticItems: structureMenuItems.getStaticMenuItems(),
    };
  }, [
    currentStructureStorage.isDeleteInProgress,
    currentStructureStorage.parentEntityFromPath.id,
    currentStructureStorage.structureId,
  ]);

  const canUserEditStructure = useMemo(
    () =>
      permissionsService.userHasOneOfThePermissions([
        EditSewerObject,
        EditSewerObjectFromAssignedProject,
      ]),
    []
  );
  const handleEditClick = () => {
    history.push(`${history.location.pathname}/edit`);
  };

  const canUserDeleteStructure = useMemo(
    () =>
      permissionsService.userHasOneOfThePermissions([
        DeleteSewerObject,
        DeleteSewerObjectsFromAssignedProject,
      ]) && currentStructureStorage.currentStructure.canDelete,
    [currentStructureStorage.currentStructure.canDelete]
  );
  const tooltipDeleteStructure = useMemo(
    () =>
      currentStructureStorage.currentStructure.canDelete
        ? undefined
        : "ViewStructure.delete.linkedobject.tooltip",
    [currentStructureStorage.currentStructure.canDelete]
  );

  const handleDeleteStructure = async () => {
    try {
      await currentStructureStorage.delete();
      history.push(`${navigatePath(history.location.pathname, -2)}`);
      snackbarNotificationsService.showSuccess("ViewStructure.delete.success");
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.structure.delete");
      handleErrorInSnackbar(applicationError);
    }
  };

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentStructureStorage.parentEntityFromPath.type,
        name: await currentStructureStorage.getParentEntityName(currentStructureStorage.currentStructure.project.name)
        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentStructureStorage.currentStructure.name
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentStructureStorage,
    currentStructureStorage.currentStructure.name,
    currentStructureStorage.currentStructure.project.name,
    currentStructureStorage.parentEntityFromPath.type,
    history.location.pathname
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewSewerObject, ViewSewerObjectFromAssignedProject]}
      titleKey="ViewStructure.title"
      titleContext={{ structureName: currentStructureStorage.currentStructure.name }}
      id="view-structure"
      menuItems={menuItems}
      isLoading={currentStructureStorage.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <EditButton disabled={!canUserEditStructure} onClick={handleEditClick} />
          <DeleteButton
            disabled={!canUserDeleteStructure}
            tooltipKey={tooltipDeleteStructure}
            confirmationAction={handleDeleteStructure}
            confirmationModalTitleKey="ViewStructure.delete"
            confirmationModalSubtitleKey="ViewStructure.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <GenericObjectIssues
        collapsibleService={collapsibleService}
        genericIssues={currentStructureStorage.currentStructure.genericIssues}
      />
      <DataSection labelKey="general.structure.details">
        <SubSection>
          <DisplaySewerObjectId
            id={currentStructureStorage.currentStructure.name}
            labelKey="sewer-objects.DisplayStructureId.label"
          />
          <DisplayDataOwner dataOwner={currentStructureStorage.currentStructure.dataOwner} />
        </SubSection>
        <SubSection>
          <DisplayInProject
            project={currentStructureStorage.currentStructure.project}
            enableRedirect
          />
          <DisplayOpenJobs openJobs={currentStructureStorage.currentStructure.openJobs} />
        </SubSection>
        <SubSection>
          <DisplayDateCreated date={currentStructureStorage.currentStructure.created} />
          <DisplayCreatedBy createdBy={currentStructureStorage.currentStructure.createdBy.name} />
        </SubSection>
      </DataSection>
      <DisplayDynamicProperties
        labelKey="general.characteristics"
        properties={currentStructureStorage.currentStructure.getDynamicPropertiesForView()}
      />
    </PageLayout>
  );
});
